import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { TranslatePipe } from '@ngx-translate/core';
import { SapSearchProgress, SapSearchStatus } from '@monorepo/interfaces';
import { WorkflowDataState } from '../../state/workflow/workflow-data.state';

@Component({
    selector: 'app-sap-search-progress',
    standalone: true,
    imports: [TranslatePipe],
    templateUrl: './sap-search-progress.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SapSearchProgressComponent {
    private readonly store = inject(Store);

    protected readonly progress = this.store.selectSignal(WorkflowDataState.sapSearchProgress);

    protected readonly visible = computed(() => this.progress() !== undefined);

    protected readonly currentCount = computed(() => {
        const p = this.progress();
        if (!p) {
            return 0;
        }
        switch (p.status) {
            case SapSearchStatus.SAVING_WORKFLOW:
            case SapSearchStatus.INITIALIZING_WORKFLOW:
            case SapSearchStatus.COMPLETED:
                return p.itemsSaved;
            default:
                return p.itemsFetched;
        }
    });

    protected readonly percentage = computed(() => {
        const p = this.progress();
        if (!p) {
            return 0;
        }
        if (p.status === SapSearchStatus.COMPLETED) {
            return 100;
        }
        if (!p.totalItems || p.totalItems === 0) {
            return 0;
        }
        // Fetch phase fills 0→50%, save phase fills 50→100%
        const fetchRatio = Math.min(1, p.itemsFetched / p.totalItems);
        const saveRatio = Math.min(1, p.itemsSaved / p.totalItems);
        return Math.min(100, Math.round((fetchRatio + saveRatio) * 50));
    });

    protected readonly phaseKey = computed(() => {
        const p = this.progress();
        if (!p) {
            return 'sap-search-progress.initializing';
        }
        switch (p.status) {
            case SapSearchStatus.INITIALIZING:
                return 'sap-search-progress.initializing';
            case SapSearchStatus.FETCHING_PRODUCTS:
                return 'sap-search-progress.fetching';
            case SapSearchStatus.SAVING_WORKFLOW:
                return 'sap-search-progress.saving';
            case SapSearchStatus.INITIALIZING_WORKFLOW:
                return 'sap-search-progress.initializing-workflow';
            case SapSearchStatus.COMPLETED:
                return 'sap-search-progress.completed';
            case SapSearchStatus.ERROR:
                return 'sap-search-progress.error';
            default:
                return 'sap-search-progress.initializing';
        }
    });

    protected readonly isError = computed(() => this.progress()?.status === SapSearchStatus.ERROR);

    protected readonly isDeterminate = computed(() => {
        const p = this.progress();
        return !!p && p.totalItems > 0;
    });

    protected readonly progressForAria = computed((): SapSearchProgress | undefined => this.progress());
}
